import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always required
    analytics: false,
    marketing: false,
    functional: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      // Show banner after a short delay
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true,
    };
    localStorage.setItem('cookieConsent', JSON.stringify(allAccepted));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setIsVisible(false);
    // Here you would initialize analytics, marketing scripts, etc.
    console.log('All cookies accepted:', allAccepted);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false,
    };
    localStorage.setItem('cookieConsent', JSON.stringify(onlyNecessary));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setIsVisible(false);
    console.log('Only necessary cookies:', onlyNecessary);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookieConsent', JSON.stringify(preferences));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
    setIsVisible(false);
    setShowPreferences(false);
    console.log('Custom preferences saved:', preferences);
  };

  const togglePreference = (key) => {
    if (key === 'necessary') return; // Can't disable necessary cookies
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const cookieTypes = [
    {
      key: 'necessary',
      title: 'Strictly Necessary',
      description: 'Essential for the website to function. Cannot be disabled.',
      required: true,
    },
    {
      key: 'functional',
      title: 'Functional',
      description: 'Enable personalized features and remember your preferences.',
      required: false,
    },
    {
      key: 'analytics',
      title: 'Analytics',
      description: 'Help us understand how visitors interact with our website.',
      required: false,
    },
    {
      key: 'marketing',
      title: 'Marketing',
      description: 'Used to deliver relevant ads and track campaign performance.',
      required: false,
    },
  ];

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Main Banner */}
          {!showPreferences && (
            <motion.div
              className="fixed bottom-0 left-0 right-0 z-50 p-4 md:p-6"
              initial={{ y: 100, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 100, opacity: 0 }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
            >
              <div className="max-w-6xl mx-auto">
                <div 
                  className="bg-dark-deeper/95 backdrop-blur-xl border border-neon-orange/20 rounded-2xl p-6 shadow-2xl"
                  style={{ boxShadow: '0 -10px 40px rgba(0, 0, 0, 0.3), 0 0 60px rgba(253, 128, 46, 0.1)' }}
                >
                  <div className="flex flex-col lg:flex-row lg:items-center gap-6">
                    {/* Cookie Icon & Text */}
                    <div className="flex-1">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 rounded-xl bg-neon-orange/20 flex items-center justify-center flex-shrink-0">
                          <svg className="w-6 h-6 text-neon-orange" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                        </div>
                        <div>
                          <h3 className="text-white font-heading font-bold text-lg mb-2">
                            We value your privacy 🍪
                          </h3>
                          <p className="text-gray-400 text-sm leading-relaxed">
                            We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. 
                            By clicking "Accept All", you consent to our use of cookies. Read our{' '}
                            <Link to="/cookie" className="text-neon-orange hover:underline">Cookie Policy</Link>
                            {' '}and{' '}
                            <Link to="/privacy" className="text-neon-orange hover:underline">Privacy Policy</Link>
                            {' '}to learn more.
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Buttons */}
                    <div className="flex flex-col sm:flex-row gap-3 lg:flex-shrink-0">
                      <motion.button
                        onClick={() => setShowPreferences(true)}
                        className="px-5 py-2.5 text-gray-400 hover:text-white text-sm font-medium border border-gray-700 hover:border-neon-orange/50 rounded-full transition-colors"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        Customize
                      </motion.button>
                      <motion.button
                        onClick={handleRejectAll}
                        className="px-5 py-2.5 text-neon-orange text-sm font-medium border border-neon-orange/30 hover:bg-neon-orange/10 rounded-full transition-colors"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        Reject All
                      </motion.button>
                      <motion.button
                        onClick={handleAcceptAll}
                        className="px-6 py-2.5 bg-neon-orange text-dark-base text-sm font-bold rounded-full"
                        whileHover={{ scale: 1.05, boxShadow: '0 0 25px rgba(253, 128, 46, 0.5)' }}
                        whileTap={{ scale: 0.95 }}
                      >
                        Accept All
                      </motion.button>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* Preferences Modal */}
          {showPreferences && (
            <motion.div
              className="fixed inset-0 z-50 flex items-center justify-center p-4"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              {/* Backdrop */}
              <motion.div
                className="absolute inset-0 bg-black/80 backdrop-blur-sm"
                onClick={() => setShowPreferences(false)}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
              />

              {/* Modal */}
              <motion.div
                className="relative bg-dark-deeper border border-neon-orange/20 rounded-2xl max-w-lg w-full max-h-[85vh] overflow-hidden"
                initial={{ scale: 0.9, opacity: 0, y: 20 }}
                animate={{ scale: 1, opacity: 1, y: 0 }}
                exit={{ scale: 0.9, opacity: 0, y: 20 }}
                style={{ boxShadow: '0 0 60px rgba(253, 128, 46, 0.15)' }}
              >
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-neon-orange/10">
                  <div>
                    <h3 className="text-xl font-heading font-bold text-white">Cookie Preferences</h3>
                    <p className="text-gray-400 text-sm mt-1">Manage your cookie settings</p>
                  </div>
                  <button
                    onClick={() => setShowPreferences(false)}
                    className="w-10 h-10 rounded-full bg-dark-base flex items-center justify-center text-gray-400 hover:text-white hover:bg-neon-orange/20 transition-colors"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                {/* Cookie Types */}
                <div className="p-6 space-y-4 max-h-[50vh] overflow-y-auto">
                  {cookieTypes.map((cookie) => (
                    <div
                      key={cookie.key}
                      className="bg-dark-base rounded-xl p-4 border border-gray-800"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <h4 className="text-white font-semibold">{cookie.title}</h4>
                        <button
                          onClick={() => togglePreference(cookie.key)}
                          disabled={cookie.required}
                          className={`relative w-12 h-6 rounded-full transition-colors ${
                            preferences[cookie.key]
                              ? 'bg-neon-orange'
                              : 'bg-gray-700'
                          } ${cookie.required ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                        >
                          <motion.div
                            className="absolute top-1 w-4 h-4 bg-white rounded-full shadow"
                            animate={{ x: preferences[cookie.key] ? 26 : 4 }}
                            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                          />
                        </button>
                      </div>
                      <p className="text-gray-400 text-sm">{cookie.description}</p>
                      {cookie.required && (
                        <span className="inline-block mt-2 text-xs text-neon-orange bg-neon-orange/10 px-2 py-1 rounded">
                          Always Active
                        </span>
                      )}
                    </div>
                  ))}
                </div>

                {/* Footer */}
                <div className="p-6 border-t border-neon-orange/10 flex flex-col sm:flex-row gap-3">
                  <motion.button
                    onClick={handleRejectAll}
                    className="flex-1 px-5 py-3 text-gray-400 text-sm font-medium border border-gray-700 hover:border-neon-orange/50 rounded-full transition-colors"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Reject All
                  </motion.button>
                  <motion.button
                    onClick={handleSavePreferences}
                    className="flex-1 px-5 py-3 bg-neon-orange text-dark-base text-sm font-bold rounded-full"
                    whileHover={{ scale: 1.02, boxShadow: '0 0 25px rgba(253, 128, 46, 0.5)' }}
                    whileTap={{ scale: 0.98 }}
                  >
                    Save Preferences
                  </motion.button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </>
      )}
    </AnimatePresence>
  );
};

export default CookieBanner;
